import React, { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
} from '@mui/icons-material'
import { CompanyCard } from './CompanyCard'
import CompanyRoomsDialog from './CompanyRoomsDialog'
import { CompanyDialog } from '../dialogs/CompanyDialog'
import { InviteUserDialog } from '../dialogs/InviteUserDialog'
import { EditUserDialog } from '../dialogs/EditUserDialog'
import { UsersDialog } from '../dialogs/UsersDialog'
import { SuspendCompanyDialog } from '../dialogs/SuspendCompanyDialog'
import { RemoveCompanyDialog, type CompanyRemovalPreview } from '../dialogs/RemoveCompanyDialog'
import { CompanyWithStats } from '../../types'
import { useCompanyManagement } from '../../hooks/useCompanyManagement'
import { CompanyService } from '../../../../services/CompanyService'
import { addNotification } from '../../../../stores/NotificationStore'
import store from '../../../../stores'
import CompanyEmailTemplates from '../../../EmailTemplateManager/CompanyEmailTemplates'
import { useDialogRouting } from '../../../../hooks/useDialogRouting'
import { CompanyBrandedShell } from '../CompanyBrandedShell'
import { memberHasNoRole } from '../../utils/roleOptions'

interface PlatformAdminViewProps {
  onBack?: () => void
  isPlatformAdmin: boolean
  isGlobalAdmin: boolean
  currentCompany: any
}

export const PlatformAdminView: React.FC<PlatformAdminViewProps> = ({
  onBack,
  isPlatformAdmin,
  isGlobalAdmin,
  currentCompany,
}) => {
  // Dialog routing integration
  const dialogRouting = useDialogRouting()
  
  const [selectedCompanyForUsers, setSelectedCompanyForUsers] = React.useState<CompanyWithStats | null>(null)
  const [syncingUsers, setSyncingUsers] = React.useState(false)
  const [selectedCompanyForRooms, setSelectedCompanyForRooms] = React.useState<CompanyWithStats | null>(null)
  const [roomsDialogOpen, setRoomsDialogOpen] = React.useState(false)
  const [emailTemplateDialogOpen, setEmailTemplateDialogOpen] = React.useState(false)
  const [selectedCompanyForTemplates, setSelectedCompanyForTemplates] = React.useState<CompanyWithStats | null>(null)
  const [removeCompanyDialogOpen, setRemoveCompanyDialogOpen] = React.useState(false)
  const [companyToRemove, setCompanyToRemove] = React.useState<CompanyWithStats | null>(null)
  const [removalReason, setRemovalReason] = React.useState('')
  const [isRemovingCompany, setIsRemovingCompany] = React.useState(false)
  const [removalPreview, setRemovalPreview] = React.useState<CompanyRemovalPreview | null>(null)
  const [removalPreviewLoading, setRemovalPreviewLoading] = React.useState(false)
  const [removalPreviewError, setRemovalPreviewError] = React.useState<string | null>(null)
  // Guards against a slow preview for a company the admin has since closed or switched away from.
  const removalPreviewRequestRef = React.useRef<string | null>(null)
  const [companyToSuspend, setCompanyToSuspend] = React.useState<CompanyWithStats | null>(null)
  const [suspendReason, setSuspendReason] = React.useState('')
  const [isSuspending, setIsSuspending] = React.useState(false)

  const {
    companies,
    loading,
    dialogs,
    companyForm,
    inviteForm,
    editUserForm,
    companyUsers,
    userMenu,
    selectedItems,
    setCompanyForm,
    setInviteForm,
    setEditUserForm,
    setSelectedItems,
    handleCreateCompany,
    handleInviteUser,
    handleEditCompany,
    handleEditCompanyOpen,
    handleEditUser,
    handleEditUserOpen,
    handleRemoveUser,
    handleUserMenuOpen,
    handleUserMenuClose,
    openDialog,
    closeDialog,
    loadCompanyUsers,
    loadCompanies,
  } = useCompanyManagement(isPlatformAdmin, false, isGlobalAdmin, currentCompany)

  const handleViewUsers = (company: CompanyWithStats) => {
    setSelectedCompanyForUsers(company)
    setSelectedItems(prev => ({ ...prev, selectedCompany: company }))
    loadCompanyUsers(company.id)
    dialogRouting.openDialog('users')
  }

  const handleSelectCompany = (company: CompanyWithStats) => {
    setSelectedItems(prev => ({ ...prev, selectedCompany: company }))
    notify(`Selected company: ${company.name}`, 'success', 3000)
  }

  const handleManageRooms = (company: CompanyWithStats) => {
    setSelectedCompanyForRooms(company)
    setRoomsDialogOpen(true)
  }

  const handleManageEmailTemplates = (company?: CompanyWithStats) => {
    setSelectedCompanyForTemplates(company || null)
    setEmailTemplateDialogOpen(true)
  }

  const handleCloseUsersDialog = () => {
    dialogRouting.closeDialog('companyManagement')
    setSelectedCompanyForUsers(null)
  }

  const handleCloseEmailTemplateDialog = () => {
    setEmailTemplateDialogOpen(false)
    setSelectedCompanyForTemplates(null)
  }

  const handleSyncUnassignedUsers = async () => {
    if (!selectedCompanyForUsers) return
    
    try {
      setSyncingUsers(true)
      const result = await CompanyService.syncUnassignedUsers(selectedCompanyForUsers.id)
      
      if (result.success) {
        // Reload users after sync
        loadCompanyUsers(selectedCompanyForUsers.id)
        // Reload companies to update user counts
        loadCompanies()
        notify(`Successfully synced ${result.syncedCount || 0} users to company`, 'success')
      }
    } catch (error) {
      console.error('Error syncing unassigned users:', error)
      notify('Error syncing unassigned users', 'error')
    } finally {
      setSyncingUsers(false)
    }
  }

  const handleInviteUserFromUsersDialog = () => {
    dialogRouting.closeDialog('companyManagement')
    // Ensure the selected company is preserved for the invite dialog
    if (selectedCompanyForUsers) {
      setSelectedItems(prev => ({ ...prev, selectedCompany: selectedCompanyForUsers }))
    }
    openDialog('showInviteDialog')
  }

  // Override the handleInviteUser to include refresh and reopen UsersDialog
  const handleInviteUserWithRefresh = async () => {
    await handleInviteUser()
    // Refresh users after inviting user and reopen UsersDialog to show updated list
    setTimeout(() => {
      if (selectedCompanyForUsers) {
        loadCompanyUsers(selectedCompanyForUsers.id)
        // Reopen UsersDialog to show the updated user list (including pending users)
        dialogRouting.openDialog('companyManagement')
      }
    }, 200) // Much faster - 200ms instead of 1000ms
  }

  const handleEditUserFromMenu = () => {
    handleEditUserOpen(userMenu.selectedUserForMenu!)
  }

  const handleRemoveUserFromMenu = () => {
    handleRemoveUser()
    // Reload companies to update user counts after removal
    setTimeout(() => {
      loadCompanies()
    }, 200) // Faster refresh
  }

  // `addNotification(...)` only builds the Redux action; without a dispatch the toast never shows.
  const notify = (message: string, type: 'success' | 'error' | 'warning' | 'info', duration = 5000) => {
    store.dispatch(addNotification({ message, type, duration }))
  }

  const updateCompanyActiveState = async (
    company: CompanyWithStats,
    isActive: boolean,
    reason: string
  ): Promise<boolean> => {
    try {
      const result = await CompanyService.updateCompanyStatus(company.id, {
        is_active: isActive,
        reason,
      })

      if (result.success) {
        notify(
          isActive
            ? `Company "${company.name}" has been reactivated.`
            : `Company "${company.name}" has been suspended. Members in its rooms were disconnected.`,
          'success'
        )
        loadCompanies()
        return true
      }
      if (result.code === 'COMPANY_DELETION_IN_PROGRESS') {
        // The card was stale: the company is half deleted. Refreshing turns it into a
        // "Deletion incomplete" card, where Delete finishes the job.
        notify(result.error || 'Company deletion is in progress.', 'error', 8000)
        loadCompanies()
        return false
      }
      notify(result.error || `Failed to ${isActive ? 'reactivate' : 'suspend'} company`, 'error')
    } catch (error) {
      console.error('Error toggling company status:', error)
      notify('Error updating company status', 'error')
    }
    return false
  }

  // Suspending disconnects people, so it goes through a confirmation; reactivating only restores
  // access and runs straight away.
  const handleToggleCompanyStatus = async (company: CompanyWithStats, isActive: boolean) => {
    if (!isActive) {
      setSuspendReason('')
      setCompanyToSuspend(company)
      return
    }
    await updateCompanyActiveState(company, true, 'Company reactivated by platform admin')
  }

  const handleCloseSuspendCompany = () => {
    if (isSuspending) return
    setCompanyToSuspend(null)
    setSuspendReason('')
  }

  const handleConfirmSuspendCompany = async () => {
    if (!companyToSuspend) return
    setIsSuspending(true)
    const ok = await updateCompanyActiveState(
      companyToSuspend,
      false,
      suspendReason.trim() || 'Company suspended by platform admin'
    )
    setIsSuspending(false)
    if (ok) {
      setCompanyToSuspend(null)
      setSuspendReason('')
    }
  }

  const handleEditCompanyLocal = (company: CompanyWithStats) => {
    handleEditCompanyOpen(company)
  }

  const handleRemoveCompany = async (company: CompanyWithStats) => {
    setCompanyToRemove(company)
    setRemovalReason('')
    setRemovalPreview(null)
    setRemovalPreviewError(null)
    setRemovalPreviewLoading(true)
    setRemoveCompanyDialogOpen(true)
    removalPreviewRequestRef.current = company.id

    const result = await CompanyService.getCompanyRemovalPreview(company.id)
    if (removalPreviewRequestRef.current !== company.id) return

    if (result.success && result.data?.impact) {
      setRemovalPreview(result.data as CompanyRemovalPreview)
    } else {
      setRemovalPreviewError(result.error || 'The server returned no preview')
    }
    setRemovalPreviewLoading(false)
  }

  const handleCloseRemoveCompany = () => {
    if (isRemovingCompany) return
    removalPreviewRequestRef.current = null
    setRemoveCompanyDialogOpen(false)
    setCompanyToRemove(null)
    setRemovalReason('')
    setRemovalPreview(null)
    setRemovalPreviewError(null)
    setRemovalPreviewLoading(false)
  }

  const handleConfirmRemoveCompany = async () => {
    if (!companyToRemove || !removalPreview) return
    const company = companyToRemove

    setIsRemovingCompany(true)
    let closeDialog = false
    try {
      const result = await CompanyService.removeCompany(
        company.id,
        removalReason.trim() || 'Company removal requested by admin'
      )

      if (result.success) {
        if (result.data?.alreadyDeleted) {
          notify(`Company "${company.name}" was already deleted.`, 'info', 6000)
        } else {
          const deleted = Number(result.data?.deletedAccounts) || 0
          const kept = Number(result.data?.keptAccounts) || 0
          notify(
            `Company "${company.name}" has been deleted. ` +
              `${deleted} ${deleted === 1 ? 'account was' : 'accounts were'} permanently deleted; ` +
              `${kept} ${kept === 1 ? 'account was' : 'accounts were'} kept.`,
            'success',
            8000
          )
        }
        closeDialog = true
        loadCompanies()
      } else if (result.code === 'STRIPE_CANCEL_FAILED') {
        // The server stopped before deleting anything else and left the company `deleting`;
        // the card now offers Delete again, and a retry resumes from the Stripe step.
        notify(
          `Could not cancel the Stripe subscription for "${company.name}", so the deletion stopped. ` +
            'The company is marked "Deletion incomplete". Click Delete again to retry.',
          'error',
          10000
        )
        closeDialog = true
        loadCompanies()
      } else {
        notify(`Error deleting company: ${result.error || 'Unknown error'}`, 'error')
      }
    } catch (error) {
      console.error('Error removing company:', error)
      notify('Failed to delete company. Please try again.', 'error')
    } finally {
      setIsRemovingCompany(false)
    }
    if (closeDialog) {
      removalPreviewRequestRef.current = null
      setRemoveCompanyDialogOpen(false)
      setCompanyToRemove(null)
      setRemovalReason('')
      setRemovalPreview(null)
      setRemovalPreviewError(null)
    }
  }

  // The roster in the hook is whichever company's users were loaded last, and the dialog may
  // invite into another one. Only rows of the company the dialog is given count as members, so a
  // re-invite keeps the member's role without showing, or trusting, a role from a different
  // company. No roster is fetched here: when the invited company's users are not the ones loaded,
  // the dialog treats every address as a new person.
  const inviteTargetCompanyId = selectedItems.selectedCompany?.id
  const inviteMembers = React.useMemo(() => {
    if (!inviteTargetCompanyId) return undefined
    const rows = companyUsers.filter((u) => u.company_id === inviteTargetCompanyId)
    return rows.length > 0 ? rows : undefined
  }, [companyUsers, inviteTargetCompanyId])

  // Deleted companies are tombstones kept only to link billing and logs. The cloud list filters
  // them out; this is the client-side backstop for an older cloud deployment.
  const visibleCompanies = companies.filter((company) => (company.status as string) !== 'deleted')

  return (
    <CompanyBrandedShell company={currentCompany ?? null}>
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        {onBack && (
          <Button
            onClick={onBack}
            startIcon={<ArrowBackIcon />}
            sx={{ mr: 2 }}
            variant="outlined"
          >
            Back
          </Button>
        )}
        <Typography variant="h4" sx={{ 
          color: '#111827', 
          fontWeight: 600,
          fontSize: '1.875rem',
          lineHeight: 1.25,
          fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
          letterSpacing: '-0.025em'
        }}>
          Company Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
          <Button
            variant="outlined"
            startIcon={<EmailIcon />}
            onClick={() => handleManageEmailTemplates()}
            sx={{ mr: 1 }}
          >
            Global Templates
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openDialog('showCreateDialog')}
          >
            Create Company
          </Button>
        </Box>
      </Box>

      {loading.loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ 
          padding: '1.5rem', 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(20rem, 1fr))', 
          gap: '1.5rem' 
        }}>
          {visibleCompanies.map((company) => (
            <CompanyCard
              key={company.id}
              company={company}
              onViewUsers={handleViewUsers}
              onEditCompany={handleEditCompanyLocal}
              onToggleStatus={handleToggleCompanyStatus}
              onSelectCompany={handleSelectCompany}
              onManageRooms={handleManageRooms}
              onManageEmailTemplates={handleManageEmailTemplates}
              onRemoveCompany={handleRemoveCompany}
              isPlatformAdmin={isPlatformAdmin}
            />
          ))}
        </Box>
      )}


      {/* Create Company Dialog */}
      <CompanyDialog
        open={dialogs.showCreateDialog}
        onClose={() => closeDialog('showCreateDialog')}
        onSubmit={handleCreateCompany}
        formData={companyForm}
        onFormChange={setCompanyForm}
        mode="create"
        isGlobalAdmin={isGlobalAdmin}
      />

      {/* Invite User Dialog */}
      <InviteUserDialog
        open={dialogs.showInviteDialog}
        onClose={() => closeDialog('showInviteDialog')}
        onSubmit={handleInviteUserWithRefresh}
        formData={inviteForm}
        onFormChange={setInviteForm}
        companyId={selectedItems.selectedCompany?.id}
        members={inviteMembers}
        isGlobalAdmin={isGlobalAdmin}
      />

      {/* Edit Company Dialog */}
      <CompanyDialog
        open={dialogs.showEditCompanyDialog}
        onClose={() => closeDialog('showEditCompanyDialog')}
        onSubmit={() => handleEditCompany()}
        formData={companyForm}
        onFormChange={setCompanyForm}
        mode="edit"
        isGlobalAdmin={isGlobalAdmin}
      />

      {/* Edit User Dialog */}
      <EditUserDialog
        open={dialogs.showEditUserDialog}
        onClose={() => closeDialog('showEditUserDialog')}
        onSubmit={handleEditUser}
        formData={editUserForm}
        onFormChange={setEditUserForm}
        userEmail={selectedItems.selectedUser?.user?.email}
        roleless={memberHasNoRole(selectedItems.selectedUser)}
        // The company the save goes to (handleEditUser), so the role names are that company's.
        companyId={(selectedItems.selectedCompany || currentCompany)?.id}
      />

      {/* Users Dialog */}
      <UsersDialog
        open={dialogRouting.isDialogOpen('users')}
        onClose={handleCloseUsersDialog}
        company={selectedCompanyForUsers}
        users={companyUsers}
        onEditUser={handleEditUserOpen}
        onRemoveUser={handleRemoveUser}
        onInviteUser={handleInviteUserFromUsersDialog}
        loading={loading.loadingUsers}
        userMenuAnchor={userMenu.userMenuAnchor}
        selectedUserForMenu={userMenu.selectedUserForMenu}
        onUserMenuOpen={handleUserMenuOpen}
        onUserMenuClose={handleUserMenuClose}
        onRemoveUserFromMenu={handleRemoveUserFromMenu}
        onEditUserFromMenu={handleEditUserFromMenu}
        onSyncUnassignedUsers={handleSyncUnassignedUsers}
        syncingUsers={syncingUsers}
        onRefreshUsers={() => selectedCompanyForUsers && loadCompanyUsers(selectedCompanyForUsers.id)}
      />

      {/* Company Rooms Dialog */}
      <CompanyRoomsDialog
        open={roomsDialogOpen}
        onClose={() => setRoomsDialogOpen(false)}
        company={selectedCompanyForRooms}
        isPlatformAdmin={isPlatformAdmin}
      />

      {/* Email Template Manager Dialog */}
      <Dialog
        open={emailTemplateDialogOpen}
        onClose={handleCloseEmailTemplateDialog}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
            backgroundColor: 'rgba(34, 38, 57, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: 'rgba(0, 0, 0, 0.2)', 
          color: 'white',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <EmailIcon sx={{ color: '#42eacb' }} />
            <Typography variant="h6">
              {selectedCompanyForTemplates ? `${selectedCompanyForTemplates.name} Email Templates` : 'Global Email Templates'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ padding: 0, overflow: 'hidden', height: 580 }}>
          {selectedCompanyForTemplates?.id ? (
            <CompanyEmailTemplates
              companyId={selectedCompanyForTemplates.id}
              // This view is only reachable by platform or global admins (CompanyManagementPanel).
              elevatedView
            />
          ) : (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" sx={{ color: '#9e9e9e', gap: 1, p: 4 }}>
              <Typography variant="body2" sx={{ textAlign: 'center' }}>
                Select a company to manage its email templates, or edit the server-side templates in{' '}
                <code>server/emails/templates/</code>.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          backgroundColor: 'rgba(0, 0, 0, 0.2)', 
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          padding: '16px 24px'
        }}>
          <Button 
            onClick={handleCloseEmailTemplateDialog}
            variant="outlined"
            sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <SuspendCompanyDialog
        open={!!companyToSuspend}
        company={companyToSuspend}
        reason={suspendReason}
        onReasonChange={setSuspendReason}
        submitting={isSuspending}
        onClose={handleCloseSuspendCompany}
        onConfirm={handleConfirmSuspendCompany}
      />

      <RemoveCompanyDialog
        open={removeCompanyDialogOpen}
        company={companyToRemove}
        preview={removalPreview}
        previewLoading={removalPreviewLoading}
        previewError={removalPreviewError}
        reason={removalReason}
        onReasonChange={setRemovalReason}
        removing={isRemovingCompany}
        onClose={handleCloseRemoveCompany}
        onConfirm={handleConfirmRemoveCompany}
      />
    </Box>
    </CompanyBrandedShell>
  )
} 